// export-addresses-dialog.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FilterComponent } from 'app/common/components/filter/filter.component';
import { BusinessPartnersFiltersComponent } from 'app/modules/components/filters/business-partners-filters/business-partners-filters.component';
import { ConfigurationFiltersComponent } from 'app/modules/components/filters/configuration-filters/configuration-filters.component';
import { RoutesFiltersComponent } from 'app/modules/components/filters/routes-filters/routes-filters.component';
import { FuseAlertComponent } from '@fuse/components/alert';
import { BusinessPartnerGroupService } from 'app/modules/components/business-partners/business-partner-group/business-partner-group.service';
import BusinessPartnerGroupAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-group/business-partner-group.entity';
import { IRequestResult } from 'app/common/interfaces/request-result.interface';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { BusinessPartnerTypeService } from 'app/modules/components/business-partners/business-partner-type/business-partner-type.service';
import BusinessPartnerTypeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner-type/business-partner-type.entity';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { filter, map, Observable } from 'rxjs';
import { IFilters } from 'app/modules/components/filters/interfaces/filterInterface';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { BusinessPartnerTerritorieExport } from 'app/modules/components/business-partners/business-partner/exports/business-partner-territories.export';
import { ExportFileFormat, ExportRange } from 'app/layout/export-overlay/tasks/core/export-task.enum';
import { ConfirmationModalType } from '../confirmation-modal';
import { ConfirmationService } from 'app/shared/services/confirmation.service';
import { environment as env } from "../../../../environments/environment.prod";
import { FormSectionComponent } from '../form-ui/form-section/form-section.component';

@Component({
  selector: "app-export-addresses-dialog",
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    FuseAlertComponent,
    FilterComponent,
    ConfigurationFiltersComponent,
    BusinessPartnersFiltersComponent,
    RoutesFiltersComponent,
    MatFormFieldModule,
    MatSelectModule,
    FormSectionComponent,
    MatOptionModule,
  ],
  providers: [BusinessPartnerTerritorieExport],
  templateUrl: "./business-partner-routes.component.html",
})
export class ExportAddressesDialogComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _dialogRef = inject(MatDialogRef<ExportAddressesDialogComponent>);
  private businessGroupService = inject(BusinessPartnerGroupService);
  private businessTypeService = inject(BusinessPartnerTypeService);
  private readonly businessService = inject(BusinessPartnerService);
  private readonly businessPartnerTerritorieExport = inject(
    BusinessPartnerTerritorieExport
  );

  form: FormGroup;
  exportType: "empty" | "filled" = "empty";
  isLoading = false;
  loadFiltersData = false;
  rolList: any[] = [];
  businessGroupList: BusinessPartnerGroupAttributes[] = [];
  businessPartnerTypeList: BusinessPartnerTypeAttributes[] = [];

  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;
  geolocationFilter: "both" | "not_registered" | "valid" = "both";
  modalTitle = " Exportar Direcciones";

  constructor(
    private readonly handlerMessage: HandlerMessageService,
    private confirmationService: ConfirmationService
  ) {}
  ngOnInit(): void {
    this.initForm();
  }

  /**
   * Inicializa el formulario de filtros
   */
  private initForm(): void {
    this.form = this._formBuilder.group({
      // Campos de business partners
      businessPartnerGroupId: [""],
      businessPartnerSubGroupId: [""],
      customerId: [""],
      businessPartnerTypeId: [""],
      routeId: [""],
      action: [""],
    });
  }

  /**
   * Cambia el tipo de exportación
   */
  onExportTypeChange(type: "empty" | "filled"): void {
    this.exportType = type;

    if (type === "empty") {
      this.form.reset();
      this.loadFiltersData = false;
    } else {
      this.loadFiltersData = true;
      if (this.businessGroupList.length === 0) {
        this.loadAllFiltersData();
        this.fetchAllBusinessTypes();
      }
    }
  }

  /**
   * Carga todos los datos necesarios para los filtros
   */
  private async loadAllFiltersData(): Promise<void> {
    this.isLoading = true;

    try {
      await Promise.all([
        this.fetchAllBusinessGroup(),
        // this.fetchAllBusinessSubGroup(),
        // this.fetchAllBusinessPartnerRol(),
        // this.fetchAllRoutes(),
        // this.fetchAllAddressTypes()
      ]);
    } catch (err) {
      this.handlerMessage.error({
        message: err?.error?.message,
        alternativeMessage: "Error al obtener grupos de socios de negocio",
        statusCode: err?.status,
      });
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Limpia los filtros
   */
  clearFilters(): void {
    this.form.reset();
  }

  /**
   * Exporta las direcciones según la configuración
   */
  onExport(): void {
    const filters = this.exportType === "filled" ? this.form.value : null;

    if (this.exportType === "empty") {
      this.downloadClienteTerritorioTemplate();
    } else {
      const noFiltersSelected =
        !filters ||
        Object.values(filters).every(
          (v) => v === null || v === "" || (Array.isArray(v) && v.length === 0)
        );

      if (noFiltersSelected) {
        this.confirmationService
          .confirm({
            title: "Atención: Exportación masiva",
            description:
              "Va a exportar todas las direcciones de los socios sin filtros. Esto puede tardar varios minutos y generar una gran cantidad de datos. ¿Desea continuar?",
            type: ConfirmationModalType.WARNING,
            descriptionIcon: "warning",
            confirmButtonText: "Continuar",
            cancelButtonText: "Cancelar",
            customWidth: 450,
          })
          .subscribe((confirmed) => {
            if (confirmed) {
              this.downloadFilledAddressTemplate(filters);
            }
          });
        return;
      } else {
        this.downloadFilledAddressTemplate(filters);
      }
    }
  }

  /**
   * Cierra el diálogo
   */
  onCancel(): void {
    this._dialogRef.close();
  }

  fetchAllBusinessGroup(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.businessGroupService.findToComboBox().subscribe({
        next: (res: IRequestResult<BusinessPartnerGroupAttributes[]>) => {
          this.businessGroupList = res.data;
          resolve();
        },
        error: (err) => {
          this.handlerMessage.error({
            message: err?.error?.message,
            alternativeMessage: "Error al obtener grupos de socios de negocio",
            statusCode: err?.status,
          });
          reject(err);
        },
      });
    });
  }

  private fetchAllBusinessTypes(): void {
    this.businessTypeService.findToComboBox().subscribe({
      next: (res) => {
        this.businessPartnerTypeList = res.data;
      },
      error: (err) => {
        this.handlerMessage.error({
          message: err?.error?.message,
          alternativeMessage: "Error al obtener tipos de socios de negocios",
          statusCode: err?.status,
        });
      },
    });
  }

  prepareFiltersAndExport(formValues: IFilters) {
    const filters: IFilters = {
      businessPartnerGroupId: formValues.businessPartnerGroupId,
      typeCustomer: formValues.businessPartnerTypeId,
      routeId: formValues.routeId,
      customerId: formValues.customerId,
      routeAreaId: formValues.routeAreaId,
      subRouteId: formValues.subRouteId,
      locationOneIds: formValues.locationOneIds,
      locationTwoIds: formValues.locationTwoIds,
      locationThreeIds: formValues.locationThreeIds,
    };
    this.businessService
      .findAllWithCount(
        { currentPage: 1, limitPerPage: 1, search: "" },
        false,
        [],
        "",
        filters
      )
      .subscribe((res) => {
        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        if (this.totalItems > 0) {
          console.log(formValues);
          this.downloadBpTerritoriesTemplate(formValues.action);
        } else {
          this.handlerMessage.warning({
            message: "No hay datos para exportar",
          });
        }
      });
  }

  downloadClienteTerritorioTemplate() {
    const url = env.BUSINESS_PARTNER_ADDRES_TERRITORIES
    window.open(url, "_blank");
  }

  /**
   * Descarga plantilla con datos filtrados
   */
  private downloadFilledAddressTemplate(filters: IFilters): void {
    this.prepareFiltersAndExport(filters);
  }

  downloadBpTerritoriesTemplate(action: string) {
    if (!this.businessPartnerTerritorieExport.canStartTask()) return;
    this.businessPartnerTerritorieExport.export(
      ExportFileFormat.Excel,
      ExportRange.AllPages,
      this.totalItems,
      this.pageSize,
      this.pageIndex,
      this.geolocationFilter,
      action
    );
  }
}